/**
 * @fileoverview The right-click menu on a Global Planning mission.
 *
 * Arthur's design from the 3 November call: right-click a mission, "Swap
 * brand ambassador", type the name into the smart dropdown, swap. A danger
 * zone at the bottom of the same menu sends a standby home on half pay.
 *
 * The swap itself is decided by the API: a checked in standby on that shift
 * covers the mission (the polygon moves to them, the BA who did not show is
 * paid nothing), anyone else simply takes the mission over. The standbys of
 * the shift are listed first because they are who ops reach for.
 */
import React, { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import ListSubheader from '@material-ui/core/ListSubheader'
import Divider from '@material-ui/core/Divider'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Button from '@material-ui/core/Button'

import SmartBASearch from 'Components/SmartBASearch'
import { SnackBarContext } from 'Components/SnackBar'
import { readGraphQLData } from 'Components/PowerBanks/hooks'
import {
  fetchMissionSwapCandidates,
  sendStandbyHome,
  swapMissionAmbassador,
} from 'Module/graphQL'

const DANGER = '#c0392b'

const styles = {
  danger: { color: DANGER, fontWeight: 600 },
  dangerHeader: {
    color: DANGER,
    lineHeight: '28px',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  candidate: selected => ({
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '8px 10px',
    marginBottom: 6,
    borderRadius: 4,
    border: `1px solid ${selected ? '#1878cf' : '#d9dee6'}`,
    background: selected ? '#eaf3fc' : '#ffffff',
    cursor: 'pointer',
  }),
  muted: { color: '#5a6b82', fontSize: 13 },
  section: { margin: '14px 0 6px', fontWeight: 600 },
}

const SwapDialog = ({ row, open, onClose, onDone }) => {
  const runSnackBar = useContext(SnackBarContext)
  const [candidates, setCandidates] = useState([])
  const [chosen, setChosen] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open || !row) return
    setChosen(null)
    let cancelled = false
    const load = async () => {
      try {
        const data = await readGraphQLData(
          await fetchMissionSwapCandidates({ missionId: row.id }),
        )
        if (!cancelled) setCandidates(data.missionSwapCandidates || [])
      } catch (error) {
        if (!cancelled) setCandidates([])
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [open, row])

  const onSwap = async () => {
    if (!chosen) return
    setBusy(true)
    try {
      const data = await readGraphQLData(
        await swapMissionAmbassador({
          missionId: row.id,
          userId: chosen.userId,
        }),
      )
      const result = data.swapMissionAmbassador
      runSnackBar({
        type: 'success',
        msg:
          result.action === 'COVER' || result.action === 'cover'
            ? `${
                chosen.name
              } covers this mission now. The BA who did not show is not paid.`
            : `${chosen.name} has this mission now.`,
      })
      onDone()
      onClose()
    } catch (error) {
      runSnackBar({
        type: 'error',
        msg: (error && error.message) || 'The swap did not go through.',
      })
    } finally {
      setBusy(false)
    }
  }

  if (!row) return null

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Swap brand ambassador</DialogTitle>
      <DialogContent>
        <div style={styles.muted}>
          {row.date} {row.time}, now with{' '}
          <strong>{row.brandAmbassador || 'nobody'}</strong>
        </div>

        <div style={styles.section}>Standby BAs on this shift</div>
        {candidates.length ? (
          candidates.map(candidate => (
            <button
              type="button"
              key={candidate.userId}
              style={styles.candidate(
                chosen && chosen.userId === candidate.userId,
              )}
              onClick={() =>
                setChosen({ userId: candidate.userId, name: candidate.name })
              }
            >
              <strong>{candidate.name}</strong>{' '}
              <span style={styles.muted}>
                {candidate.checkedIn
                  ? 'checked in, covers the polygon'
                  : 'not checked in yet'}
              </span>
            </button>
          ))
        ) : (
          <div style={styles.muted}>Nobody holds a standby seat here.</div>
        )}

        <div style={styles.section}>Or anyone else</div>
        <SmartBASearch
          cityId={row.cityId}
          onSelect={user =>
            setChosen(
              user
                ? {
                    userId: user.id,
                    name: `${user.firstName} ${user.lastName}`,
                  }
                : null,
            )
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={busy || !chosen}
          onClick={onSwap}
        >
          {chosen ? `Swap to ${chosen.name}` : 'Swap'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

SwapDialog.propTypes = {
  row: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
}

const SendHomeDialog = ({ row, open, onClose, onDone }) => {
  const runSnackBar = useContext(SnackBarContext)
  const [busy, setBusy] = useState(false)

  const onConfirm = async () => {
    setBusy(true)
    try {
      await readGraphQLData(await sendStandbyHome({ missionId: row.id }))
      runSnackBar({
        type: 'success',
        msg: `${row.brandAmbassador ||
          'The standby'} is sent home on half pay.`,
      })
      onDone()
      onClose()
    } catch (error) {
      runSnackBar({
        type: 'error',
        msg: (error && error.message) || 'Could not send them home.',
      })
    } finally {
      setBusy(false)
    }
  }

  if (!row) return null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs">
      <DialogTitle>Send home, half pay</DialogTitle>
      <DialogContent>
        <strong>{row.brandAmbassador}</strong> goes home and is paid one hour,
        half the shift. This cannot be undone from here.
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button style={{ color: DANGER }} disabled={busy} onClick={onConfirm}>
          Send home
        </Button>
      </DialogActions>
    </Dialog>
  )
}

SendHomeDialog.propTypes = {
  row: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
}

const MissionContextMenu = ({ anchor, row, onClose, onDone }) => {
  const [dialog, setDialog] = useState(null)
  const [dialogRow, setDialogRow] = useState(null)

  const openDialog = name => {
    setDialogRow(row)
    setDialog(name)
    onClose()
  }

  const closeDialog = () => setDialog(null)

  return (
    <>
      <Menu
        keepMounted
        open={!!anchor}
        onClose={onClose}
        anchorReference="anchorPosition"
        anchorPosition={
          anchor ? { top: anchor.mouseY, left: anchor.mouseX } : undefined
        }
      >
        <MenuItem onClick={() => openDialog('swap')}>
          Swap brand ambassador
        </MenuItem>
        <Divider />
        <ListSubheader style={styles.dangerHeader}>Danger zone</ListSubheader>
        <MenuItem
          disabled={!row || !row.isStandby}
          onClick={() => openDialog('sendHome')}
          style={styles.danger}
        >
          Send home, half pay
        </MenuItem>
        {row && !row.isStandby ? (
          <MenuItem disabled style={styles.muted}>
            Only for a standby seat
          </MenuItem>
        ) : null}
      </Menu>

      <SwapDialog
        row={dialogRow}
        open={dialog === 'swap'}
        onClose={closeDialog}
        onDone={onDone}
      />
      <SendHomeDialog
        row={dialogRow}
        open={dialog === 'sendHome'}
        onClose={closeDialog}
        onDone={onDone}
      />
    </>
  )
}

MissionContextMenu.propTypes = {
  anchor: PropTypes.shape({
    mouseX: PropTypes.number,
    mouseY: PropTypes.number,
  }),
  row: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
}

MissionContextMenu.defaultProps = {
  anchor: null,
  row: null,
}

export default MissionContextMenu
